/* THE STEP A NEW COACH CANNOT FIND, PUT WHERE THEY ARE STANDING — the web's half.
 *
 * 🔴 MEASURED LIVE 2026-09-22, and it is the first time this defect has had a real victim rather
 * than a reasoned one: **William Soo has 145 exercises, 4 workouts and NO `coach_profiles` row.**
 * He is the only coach who arrived through R15's web sign-up, and R15 worked — his
 * `account_role` IS `'coach'`. He built an entire library in a browser and never found the page
 * that makes him findable.
 *
 * ⚠️ **THE 🔴 IN THE QUEUE SAYS "THE COACH PROFILE IS NEVER CREATED" AND THAT IS NOT THE DEFECT.**
 * The page is built (`CoachPage.tsx`), routed, and linked. Creation works. What is missing is that
 * NOTHING EVER SAYS IT EXISTS: the only route to it is inside a collapsed `<details>` at the foot
 * of the rail, which you have to already know to open. Measuring this before building the fix the
 * entry prescribed is what this repo's own rule asks for, and it changed what got built — the
 * prescribed repair (create the row) would have handed William a published-looking page he never
 * wrote, which is worse than the silence.
 *
 * ⚠️ **SO THE REPAIR IS THE AFFORDANCE, NOT THE ROW**, which is the same conclusion the iOS side
 * reached (`CoachSetupPrompt.swift`, shipped) — *a gate that fails by showing NOTHING is
 * indistinguishable from a feature nobody built.*
 *
 * ⚠️ **THE COPY IS PORTED FROM THE PHONE, NOT WRITTEN AGAIN, AND A RULER PINS IT**
 * (`coachSetup.test.ts` reads `CoachSetupPrompt.swift` and compares). Two surfaces naming one
 * state in two ways is this repo's defining bug class, and a coach who sees one sentence in the
 * app and a different one in the browser has no way to know they are the same thing.
 *
 * ⚠️ **ONE WORDING CALL, TAKEN RATHER THAN LEFT OPEN, AND SAID OUT LOUD:** the draft sentence ends
 * *"and you can't post"*, and the web has no posting surface at all. It is kept VERBATIM because
 * the claim is about the ACCOUNT, which spans both surfaces, and because inventing a second
 * sentence here is the exact failure this file exists to prevent. Per his rule a wording call goes
 * to the open Fable session; there is none (it returns 2026-09-24), so this is one session's call
 * and it is a one-line change if Fable disagrees.
 */

/** What we know about this coach's page. `unknown` is its own answer and never falls through. */
export type CoachPageState = "unknown" | "absent" | "draft" | "published";

/** The card's three strings, or `null` when no card is drawn. */
export type CoachSetupCopy = { title: string; body: string; action: string };

/* 🔴 **IT DRAWS IN TWO STATES AND RETIRES ITSELF IN THE OTHER TWO** — the same contract the phone's
 * `shouldShow` has (`loaded && draft?.isPublished != true`).
 *
 * ⚠️ **`unknown` DRAWS NOTHING, AND THAT IS THE LOAD-BEARING CASE.** Rendering the card while the
 * answer is still in flight would flash *"set up your coaching page"* at a coach who published
 * months ago — showing it late is a delay, showing it wrongly is a lie about their account. The
 * phone's comment says the same thing in its own words.
 */
export function coachSetupCopy(state: CoachPageState): CoachSetupCopy | null {
  if (state === "absent") {
    return {
      title: "Set up your coaching page",
      body: "Clients find you through it, and you need it before you can post. It takes a minute.",
      action: "Set it up",
    };
  }
  if (state === "draft") {
    return {
      title: "Your coaching page isn't published",
      body: "It's saved as a draft. Until you publish it, clients can't find you in Coaches and you can't post.",
      action: "Finish it",
    };
  }
  return null;
}

/* Turns what the reader found into the state above.
 *
 * ⚠️ **A ROW THAT EXISTS AND A ROW THAT DOES NOT ARE DIFFERENT ANSWERS, AND `loadCoachProfile` USED
 * TO COLLAPSE THEM** — it returned `EMPTY_PROFILE` for "no row", which is byte-identical to a row
 * somebody saved empty. That is the codex's *an empty collection standing in for a negative answer*
 * shape, and it is why that reader now returns `exists` beside the profile: this decision needs the
 * distinction, because the two states send a coach to different places and say different things.
 */
export function coachPageState(
  found: { exists: boolean; isPublished: boolean } | null,
): CoachPageState {
  if (found === null) return "unknown";
  if (found.isPublished) return "published";
  return found.exists ? "draft" : "absent";
}
